import React from "react";

import { FAMILY_ORDER, FAMILY_META } from "../../data/strategyFamily";

/**
 * StrategyFamilyFilter — 유형(패밀리)별 보기 칩 행.
 *
 * '전체' + 현재 존재하는 패밀리 칩만 노출한다. 칩을 탭하면 그 유형만 집중해서
 * 볼 수 있다 (PaperOverviewPage.renderStrategyGrid 의 filterFamily 분기).
 *
 * 모바일: 좌측 "유형" 라벨 + 가로 스크롤(overflow-x-auto) 칩 행.
 * 데스크톱(sm+): 줄바꿈(flex-wrap).
 *
 * @param {object} props
 * @param {Record<string, Array>} props.grouped groupByFamily 결과
 * @param {string} props.value 'all' 또는 FAMILY_META 키
 * @param {(v: string) => void} props.onChange
 */
export default function StrategyFamilyFilter({ grouped, value, onChange }) {
    const present = FAMILY_ORDER.filter((k) => (grouped?.[k]?.length ?? 0) > 0);
    if (present.length <= 1) return null; // 한 유형뿐이면 필터가 무의미
    const total = present.reduce((s, k) => s + grouped[k].length, 0);

    return (
        <div
            className="flex items-center gap-1.5 overflow-x-auto pb-1 mb-3 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap"
            role="group"
            aria-label="전략 유형(패밀리) 필터"
        >
            <span className="text-xs text-content-muted shrink-0 mr-0.5">유형</span>
            <Chip
                selected={value === "all"}
                onClick={() => onChange("all")}
                label="전체"
                count={total}
            />
            {present.map((k) => (
                <Chip
                    key={k}
                    selected={value === k}
                    // 활성 칩 재클릭 시 전체로 토글(StatusRibbon 칩과 동일한 동작).
                    onClick={() => onChange(value === k ? "all" : k)}
                    glyph={FAMILY_META[k].glyph}
                    label={FAMILY_META[k].label}
                    count={grouped[k].length}
                />
            ))}
        </div>
    );
}

function Chip({ selected, onClick, glyph, label, count }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={selected}
            aria-label={`${label} 유형 ${count}개 보기`}
            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border min-h-11 shrink-0 whitespace-nowrap transition-colors ${
                selected
                    ? "bg-brand-subtle text-brand-dark border-brand/30"
                    : "bg-surface-card text-content-secondary border-surface-border hover:bg-surface-hover"
            }`}
        >
            {glyph && <span aria-hidden="true">{glyph}</span>}
            {label}
            <span
                className={`tabular-nums ${
                    selected ? "text-brand-dark" : "text-content-muted"
                }`}
            >
                {count}
            </span>
        </button>
    );
}
